import Phaser from "phaser";
import i18next from "i18next";
import { ModernButton } from "../../../ui/modern-button";
import { PortalSpinner } from "../../../ui/portal-spinner";
import { showConfetti } from "../../../ui/splash-particle";
import {
  SeasonLevelRow,
  SeasonRewardEntry,
  SEASON_LEVEL_THRESHOLDS,
  getSeasonProgress,
  emoticonKeyToFrame,
} from "../../../models/season";
import { fetchItemDefs } from "../../../item-catalog";
import { ItemDefinition } from "../../../models/common";
import { SpriteLoader } from "../../../sprite-loader";
import { IAPManager } from "../../../managers/iap-manager";
import { trackEvent } from "../../../utils/ga-events";
import { getSafeAreaInsetLeft } from "../../../utils/mobile-utils";

type SeasonCosmetics = {
  unlockedEmotes: string[];
  unlockedSprays: number[];
  unlockedProfileIcons: number[];
  unlockedTitles: number[];
  unlockedHats: number[];
  unlockedBorders: number[];
  equippedProfileIcon: number;
  equippedTitle: number;
  equippedHat?: number;
  equippedBorder: number;
};

interface SeasonDataPayload {
  seasonXp: number;
  level: number;
  xpIntoLevel: number;
  xpForNextLevel: number;
  isMaxLevel: boolean;
  seasonPassOwned: boolean;
  claimedSeasonLevels: { free: number[]; premium: number[] };
  rewards: SeasonLevelRow[];
  cosmetics: SeasonCosmetics;
}

type CatalogEntry = { id: number; name?: string; sprite?: string; text?: string };
type EmoteCatalogEntry = { id: number; emoticonKey: string };

type Catalogs = {
  profileIcons: CatalogEntry[];
  sprays: CatalogEntry[];
  titles: CatalogEntry[];
  hatSkins: CatalogEntry[];
  borders: CatalogEntry[];
  emotes: EmoteCatalogEntry[];
  items: ItemDefinition[];
};

let catalogsPromise: Promise<Catalogs> | null = null;
function fetchCatalogs(): Promise<Catalogs> {
  if (!catalogsPromise) {
    catalogsPromise = Promise.all([
      fetch("/data/profile-icons.json").then((r) => r.json()),
      fetch("/data/sprays.json").then((r) => r.json()),
      fetch("/data/titles.json").then((r) => r.json()),
      fetch("/data/hats.json").then((r) => r.json()),
      fetch("/data/borders.json").then((r) => r.json()),
      fetch("/data/season-emotes.json").then((r) => r.json()),
      fetchItemDefs(),
    ]).then(([profileIcons, sprays, titles, hatSkins, borders, emotes, items]) => ({
      profileIcons,
      sprays,
      titles,
      hatSkins,
      borders,
      emotes,
      items,
    }));
  }
  return catalogsPromise;
}

// Item rarity color bands (common/uncommon/rare/epic), purely cosmetic for the
// item-reveal cards — loot box contents themselves are fixed in seasons.json.
const ITEM_RARITY_COLOR = { common: 0x9ca3af, uncommon: 0x4ade80, rare: 0x60a5fa, epic: 0xc084fc };
function getItemRarityColor(price: number): number {
  if (price <= 20) return ITEM_RARITY_COLOR.common;
  if (price <= 100) return ITEM_RARITY_COLOR.uncommon;
  if (price <= 250) return ITEM_RARITY_COLOR.rare;
  return ITEM_RARITY_COLOR.epic;
}

type RewardDisplay = {
  label: string;
  spriteKey?: string;
  frame?: number;
  isCredits?: boolean;
  creditsAmount?: number;
  labelColor?: string;
  sublabel?: string;
};

type PendingReveal = { level: number; track: "free" | "premium"; reward: SeasonRewardEntry };

// Server-side IAP product catalog row (see iap_products table / get-iap-products).
type SeasonPassProduct = {
  id: string;
  product_type: string;
  apple_product_id: string | null;
  google_product_id: string | null;
};

// Closed (first) frame of each chest tier within the "lootbox-chests" spritesheet
// (chests.png, 32x32 frames, 12 cols x 8 rows — frame index = row * 12 + col).
const LOOTBOX_FRAME: Record<"bronze" | "silver" | "gold" | "platinum", number> = {
  bronze: 51, // row 4, col 3 — wood-grain chest
  silver: 54, // row 4, col 6 — gray metal chest
  gold: 9, // row 0, col 9 — ornate gold chest
  platinum: 6, // row 0, col 6 — blue/cyan chest
};

// Per-tier label color, matching each tier's chest/metal color language.
const LOOTBOX_LABEL_COLOR: Record<"bronze" | "silver" | "gold" | "platinum", string> = {
  bronze: "#c17d43",
  silver: "#cbd5e1",
  gold: "#fbbf24",
  platinum: "#67e8f9",
};

// Frame index (0-6) into the "weekly-rewards" spritesheet for a given season credits
// amount, lowest-icon-to-biggest. Season credit rewards range 500-10000 (see
// server/data/seasons.json) — well outside the daily challenge's 50-1000 range that
// sheet was drawn for, so these breakpoints split the season's own amount spread into
// 7 roughly even buckets rather than reusing the daily challenge's reward values directly.
const CREDITS_ICON_THRESHOLDS = [1250, 1750, 2500, 3500, 5000, 8000];
function getCreditsIconFrame(amount: number): number {
  let frame = 0;
  for (const threshold of CREDITS_ICON_THRESHOLDS) {
    if (amount >= threshold) frame++;
    else break;
  }
  return frame;
}

// Card art/badges are drawn at this "design" size inside buildCard(); CARD_SCALE shrinks
// the whole card uniformly when it's placed in a stacked track, so none of that drawing
// code needs to change to fit two tracks in the ~360-405px canvas height.
const CARD_W = 102;
const CARD_H = 134;
const CARD_SCALE = 0.7; // 25% bigger than the previous 0.56
const CARD_DISP_W = CARD_W * CARD_SCALE;
const CARD_DISP_H = CARD_H * CARD_SCALE;
const CARD_GAP = 8;
const CARD_STEP = CARD_DISP_W + CARD_GAP;
const CARD_TOP = 4; // margin above and below a track's cards
const TRACK_PAD = 14;
const TICKETS_PANEL_W = 168; // base width of the left panel's content area (see buildTicketsPanel)
// Always-applied minimum left margin for the tickets panel's content, so it never sits
// flush against the screen edge even with no real safe-area inset (e.g. desktop/Android,
// or Chrome's device-mode emulation, which doesn't implement env(safe-area-inset-left) at
// all). The real inset (Dynamic Island / front camera in landscape) wins when it's bigger.
const SIDEBAR_MIN_LEFT_MARGIN = 48;
// The +26 gives the track's mask enough headroom for the credits/box labels, whose
// bigger font now overflows past the card's own CARD_H once scaled down by CARD_SCALE.
const TRACK_H = CARD_DISP_H + CARD_TOP * 2 + 26;
const LABEL_H = 14; // "PREMIUM"/"FREE" section label row
const LEVEL_BAR_H = 50; // single, shared level-progress bar between the two tracks
const LEVEL_DOT_R = 6;
const LEVEL_DOT_BORDER_W = 2;
const LEVEL_BAR_LINE_H = 16;
const LEVEL_BAR_FILL_H = LEVEL_BAR_LINE_H - 4; // a bit shorter than the track so its border stays visible
const LEVEL_BAR_CENTER_Y = 10; // dot/line vertical center within the level bar row
const SECTION_GAP = 2;

// Vertical layout: premium label/track -> level progress bar (shared) -> free label/track -> close button.
const PREMIUM_LABEL_TOP = SECTION_GAP + 8;
const PREMIUM_TRACK_TOP = PREMIUM_LABEL_TOP + LABEL_H;
const LEVEL_BAR_TOP = PREMIUM_TRACK_TOP + TRACK_H + SECTION_GAP + 4;
const FREE_LABEL_TOP = LEVEL_BAR_TOP + LEVEL_BAR_H + SECTION_GAP - 8;
const FREE_TRACK_TOP = FREE_LABEL_TOP + LABEL_H;

export class SeasonScreen {
  private readonly scene: Phaser.Scene;
  private readonly panel: Phaser.GameObjects.Container;
  private readonly onClose: () => void;

  private dataListener: ((data: SeasonDataPayload | null) => void) | null = null;
  private portalSpinner: PortalSpinner | null = null;
  private catalogs: Catalogs | null = null;
  private data: SeasonDataPayload | null = null;
  private passButton: ModernButton | null = null;

  // Both reward tracks and the level dot-line share a single horizontal scroll position
  // so dragging any of them pans the whole level sequence together.
  private premiumContainer: Phaser.GameObjects.Container | null = null;
  private freeContainer: Phaser.GameObjects.Container | null = null;
  private levelBarContainer: Phaser.GameObjects.Container | null = null;
  // Unmasked (can extend above the level bar's row) so it scrolls in sync but is never clipped.
  private xpTooltipContainer: Phaser.GameObjects.Container | null = null;

  // Cached geometry from buildLevelProgressBar, reused by drawProgressFill so the bar
  // can be redrawn many times per second (e.g. mid tween) without recomputing layout.
  private levelBarGeom: {
    container: Phaser.GameObjects.Container;
    tooltipContainer: Phaser.GameObjects.Container;
    totalLevels: number;
    firstX: number;
    barW: number;
    segFrac: number;
    cy: number;
  } | null = null;
  private levelBarDynamicObjs: Phaser.GameObjects.GameObject[] = [];

  // Reward card containers keyed by `${track}-${level}`, so a claim click can update (or a
  // level-reached flash can target) one specific card without rebuilding the whole track.
  private cardRefs: Map<string, Phaser.GameObjects.Container> = new Map();
  // Currently-glowing (claimable) cards, keyed the same way — every earned, unclaimed card
  // gets an entry, since any of them can be claimed in any order. Value is the pair of
  // fire particle emitters (yellow + white core) attached to that card.
  private cardGlows: Map<string, Phaser.GameObjects.Particles.ParticleEmitter[]> = new Map();
  // Every earned-unclaimed card's pulsating background wash, keyed the same way.
  private cardPulses: Map<string, { tween: Phaser.Tweens.Tween; gfx: Phaser.GameObjects.Graphics }> = new Map();

  // Screen size cached from the last renderMain, so a claim click (which happens well after
  // render) can reopen the reward-reveal overlay without needing it threaded through.
  private screenWidth = 0;
  private screenHeight = 0;

  // Left tickets panel's full width for the current render — TICKETS_PANEL_W plus the
  // device's left safe-area inset (Dynamic Island / front camera in landscape), so the
  // panel widens to clear the camera and its content can shift right of it.
  private sidebarW = TICKETS_PANEL_W;

  // Season xp to animate the level bar up from (set only when opened from the
  // post-match flow — see BaseScene.openSeasonScreen); consumed once by renderMain.
  private animateFromXp: number | undefined = undefined;

  private scrollX = 0;
  private scrollMinX = 0;
  private scrollMaxX = 100;
  private isDragging = false;
  private dragStartX = 0;
  private scrollBaseX = 0;
  private moveHandler: ((p: Phaser.Input.Pointer) => void) | null = null;
  private upHandler: (() => void) | null = null;
  private tapDownHandler: (() => void) | null = null;
  private tapUpHandler: (() => void) | null = null;

  private activeTimers: Phaser.Time.TimerEvent[] = [];
  private activeTweens: Phaser.Tweens.Tween[] = [];

  constructor(scene: Phaser.Scene, panel: Phaser.GameObjects.Container, onClose: () => void) {
    this.scene = scene;
    this.panel = panel;
    this.onClose = onClose;
  }

  show(animateFromXp?: number, onRendered?: (data: SeasonDataPayload | null) => void): void {
    this.panel.setVisible(true);
    this.panel.removeAll(true);
    this.cleanupScrollHandlers();
    this.cleanupTimersAndTweens();
    this.premiumContainer = null;
    this.freeContainer = null;
    this.passButton = null;
    this.levelBarContainer = null;
    this.xpTooltipContainer = null;
    this.levelBarGeom = null;
    this.levelBarDynamicObjs = [];
    this.cardRefs.clear();
    this.cardGlows.clear();
    this.cardPulses.clear();
    this.scrollX = 0;
    this.animateFromXp = animateFromXp;

    this.portalSpinner = new PortalSpinner(this.scene);

    const onData = (data: SeasonDataPayload | null) => {
      if (this.dataListener) {
        window.socket?.off("season-data", this.dataListener as any);
        this.dataListener = null;
      }
      this.portalSpinner?.destroy();
      this.portalSpinner = null;

      Promise.resolve(fetchCatalogs())
        .then((catalogs) => {
          this.catalogs = catalogs;
          this.data = data;
          this.panel.removeAll(true);
          if (data) this.renderMain(data);
          else this.renderEmpty();
          onRendered?.(data);
        })
        .catch((err) => {
          console.error("SeasonScreen: failed to load cosmetic catalogs", err);
        });
    };

    this.dataListener = onData;
    window.socket?.once("season-data", onData as any);
    window.socket?.emit("get-season-data");
  }

  private renderEmpty(): void {
    const { width, height } = this.scene.scale;
    this.buildBackground(width, height);
    this.panel.add(
      this.scene.add
        .text(width / 2, height / 2, "Season data unavailable", {
          fontSize: "14px",
          fontFamily: "Arial",
          color: "#94a3b8",
        })
        .setOrigin(0.5),
    );
    this.buildCloseButton(width, height);
  }

  private renderMain(data: SeasonDataPayload): void {
    const startXp = this.animateFromXp;
    this.animateFromXp = undefined;

    const { width, height } = this.scene.scale;
    this.screenWidth = width;
    this.screenHeight = height;
    this.sidebarW = TICKETS_PANEL_W + Math.max(SIDEBAR_MIN_LEFT_MARGIN, getSafeAreaInsetLeft());

    this.buildBackground(width, height);

    // Reward content starts clear of the fixed tickets panel, and defaults to scrolled
    // to the player's current level instead of level 1. Scrolling spans the whole season.
    // Columns are 1-indexed by level (column 0 is reserved for the "level 0" starting
    // marker on the shared progress bar — see buildLevelProgressBar), so a card/dot for
    // level L sits at column L, one CARD_STEP further right than its old (level - 1) index.
    const total = data.rewards.length;
    const activeIndex = total > 0 ? Phaser.Math.Clamp(data.level, 0, total - 1) : 0;
    const activeColumn = activeIndex + 1;

    const totalContentW = (total + 1) * CARD_STEP - CARD_GAP + TRACK_PAD * 2;

    this.scrollMinX = Math.min(0, width - (this.sidebarW + totalContentW));
    this.scrollMaxX = 100;

    const activeCardCenterX = this.sidebarW + TRACK_PAD + activeColumn * CARD_STEP + CARD_DISP_W / 2;
    const visibleCenterX = this.sidebarW + (width - this.sidebarW) / 2;
    this.scrollX = Phaser.Math.Clamp(visibleCenterX - activeCardCenterX, this.scrollMinX, this.scrollMaxX);

    // Zone is set up before the tracks are built so it sits behind their cards in the
    // display list — otherwise its full-row hit area would swallow clicks meant for a
    // glowing, claimable card (Phaser input defaults to topmost-only hit handling).
    this.setupUnifiedScroll(width);

    this.buildRewardTrack(data, width, "premium", PREMIUM_TRACK_TOP);

    this.buildLevelProgressBar(data, width, LEVEL_BAR_TOP, startXp);

    this.buildRewardTrack(data, width, "free", FREE_TRACK_TOP);

    this.buildTicketsPanel();
    this.buildCloseButton(width, height);

    // Rewards are no longer auto-revealed the instant a level is reached — cards for
    // already-earned, unclaimed levels render with an "earned" border and all of them
    // glow and are clickable, in any order (see buildRewardTrack/attachCardGlow). All
    // that's left here is animating the bar filling up to the player's current xp,
    // pausing briefly on each newly-crossed level to pop its dot and flash that level's
    // reward cards.
    if (startXp === undefined || startXp >= data.seasonXp) {
      return;
    }

    // Levels the bar will pass through on its way from startXp to data.seasonXp. Each one
    // gets its own short tween up to that level's threshold, followed by a brief pop/flash
    // pause — so the bar visibly stops right where a new level was actually reached.
    const startLevel = getSeasonProgress(startXp).level;
    const crossedLevels: number[] = [];
    for (let level = startLevel + 1; level <= data.level; level++) crossedLevels.push(level);

    if (crossedLevels.length === 0) {
      const tween = this.scene.tweens.addCounter({
        from: startXp,
        to: data.seasonXp,
        duration: 700,
        onUpdate: (t) => this.drawProgressFill(t.getValue() ?? startXp),
        onComplete: () => this.drawProgressFill(data.seasonXp, true),
      });
      this.activeTweens.push(tween);
      return;
    }

    const STOP_DURATION = 550;
    const LEVEL_REACHED_PAUSE = 650; // time to admire a level's pop/flash before the bar resumes
    const playStop = (fromXp: number, stopIdx: number) => {
      if (stopIdx >= crossedLevels.length) {
        // Final leg: last crossed level's threshold up to the true (possibly mid-level)
        // seasonXp. Scaled by how much of the current level it actually covers, so a
        // small remainder doesn't take as long as a full level-to-level hop.
        if (fromXp >= data.seasonXp) {
          this.drawProgressFill(data.seasonXp, true);
          return;
        }
        const finalProgress = getSeasonProgress(data.seasonXp);
        const duration = Phaser.Math.Clamp(finalProgress.progress * STOP_DURATION, 200, STOP_DURATION);
        const tween = this.scene.tweens.addCounter({
          from: fromXp,
          to: data.seasonXp,
          duration,
          onUpdate: (t) => this.drawProgressFill(t.getValue() ?? fromXp),
          onComplete: () => this.drawProgressFill(data.seasonXp, true),
        });
        this.activeTweens.push(tween);
        return;
      }

      const level = crossedLevels[stopIdx];
      const toXp = SEASON_LEVEL_THRESHOLDS[level - 1];
      const tween = this.scene.tweens.addCounter({
        from: fromXp,
        to: toXp,
        duration: STOP_DURATION,
        onUpdate: (t) => this.drawProgressFill(t.getValue() ?? fromXp),
        onComplete: () => {
          this.drawProgressFill(toXp, true);
          this.popDot(level);
          this.flashLevelCards(level);
          const pause = this.scene.time.delayedCall(LEVEL_REACHED_PAUSE, () => playStop(toXp, stopIdx + 1));
          this.activeTimers.push(pause);
        },
      });
      this.activeTweens.push(tween);
    };

    playStop(startXp, 0);
  }

  // ── Close button ─────────────────────────────────────────────────────────────

  private buildCloseButton(width: number, height: number): void {
    const closeButton = new ModernButton({
      scene: this.scene,
      x: width / 2,
      y: height - 40,
      text: i18next.t("ui:buttons.back"),
      style: "secondary",
      onClick: () => this.onClose(),
    });
    this.panel.add(closeButton);
  }

  // ── Background ───────────────────────────────────────────────────────────────

  private buildBackground(width: number, height: number): void {
    const bg = this.scene.add.image(width / 2, height / 2, "season-bg");
    bg.setOrigin(0.5);
    const scale = Math.max(width / bg.width, height / bg.height);
    bg.setScale(scale);
    this.panel.add(bg);
  }

  // ── Season tickets panel (fixed, doesn't scroll with the reward tracks) ─────

  private buildTicketsPanel(): void {
    const { height } = this.scene.scale;
    const panelX = 0;
    const panelW = this.sidebarW;
    // Left safe-area inset (Dynamic Island / front camera in landscape) — the checkered
    // fill/border still spans the full panelW, but the icons/text/button are centered in
    // the region to the right of it so nothing sits under the camera cutout.
    const safeLeft = panelW - TICKETS_PANEL_W;
    const contentCenterX = panelX + safeLeft + TICKETS_PANEL_W / 2;
    const top = 0;
    const panelH = height;

    // Right edge is a saw-tooth of triangular spikes instead of a flat line.
    const spikeDepth = 14;
    const toothCount = Math.max(1, Math.round(panelH / 24));
    const toothH = panelH / toothCount;
    const points: Phaser.Types.Math.Vector2Like[] = [
      { x: panelX, y: top },
      { x: panelX + panelW, y: top },
    ];
    for (let i = 0; i < toothCount; i++) {
      const yStart = top + i * toothH;
      points.push({ x: panelX + panelW + spikeDepth, y: yStart + toothH / 2 });
      points.push({ x: panelX + panelW, y: yStart + toothH });
    }
    points.push({ x: panelX, y: top + panelH });

    // Checkered fill — premium/free variant of the checkered art, tiled and panned for a
    // scrolling effect, clipped to the spiky panel silhouette via a mask.
    const checkerW = panelW + spikeDepth;
    const checker = this.scene.add.tileSprite(
      panelX + checkerW / 2,
      top + panelH / 2,
      checkerW,
      panelH,
      this.data?.seasonPassOwned ? "season-checkered-premium" : "season-checkered-free",
    );
    this.panel.add(checker);

    const checkerMask = this.scene.add.graphics();
    checkerMask.fillStyle(0xffffff);
    checkerMask.fillPoints(points, true);
    checkerMask.setVisible(false);
    this.panel.add(checkerMask);
    checker.setMask(checkerMask.createGeometryMask());

    const checkerTween = this.scene.tweens.add({
      targets: checker,
      tilePositionX: 256,
      tilePositionY: 256,
      duration: 6000,
      repeat: -1,
      ease: "Linear",
    });
    this.activeTweens.push(checkerTween);

    const border = this.scene.add.graphics();
    border.lineStyle(3, 0xf59e0b, 1);
    border.strokePoints(points, true, true);
    this.panel.add(border);

    const premiumCenterY = PREMIUM_TRACK_TOP + TRACK_H / 2;
    const premiumIcon = this.scene.add.image(contentCenterX, premiumCenterY, "season-ticket-premium");
    const premiumScale = Math.min((TICKETS_PANEL_W - 16) / premiumIcon.width, (TRACK_H - 8) / premiumIcon.height);
    premiumIcon.setScale(premiumScale);
    this.panel.add(premiumIcon);

    const seasonLabelY = premiumCenterY + (premiumIcon.height * premiumScale) / 2 + 20;
    this.panel.add(
      this.scene.add
        .text(contentCenterX, seasonLabelY, "SEASON ONE", {
          fontSize: "18px",
          fontFamily: "Anton, sans-serif",
          color: "#fbbf24",
          stroke: "#000000",
          strokeThickness: 3,
        })
        .setOrigin(0.5),
    );

    if (this.data?.seasonPassOwned) {
      this.panel.add(
        this.scene.add
          .text(contentCenterX, seasonLabelY + 36, "ACTIVE", {
            fontSize: "30px",
            fontFamily: "Anton, sans-serif",
            color: "#ffffff",
            fontStyle: "600",
          })
          .setOrigin(0.5),
      );
    } else {
      const passButton = new ModernButton({
        scene: this.scene,
        x: contentCenterX,
        y: seasonLabelY + 36,
        text: "ACTIVATE",
        style: "success",
        size: "normal",
        onClick: () => this.handleActivateClick(),
      });
      this.panel.add(passButton);
      this.passButton = passButton;
    }

    const freeCenterY = FREE_TRACK_TOP + TRACK_H / 2;
    const freeIcon = this.scene.add.image(contentCenterX, freeCenterY, "season-ticket-free");
    const freeScale = Math.min((TICKETS_PANEL_W - 16) / freeIcon.width, (TRACK_H - 8) / freeIcon.height);
    freeIcon.setScale(freeScale);
    this.panel.add(freeIcon);
  }

  // ── Season pass purchase (real-money IAP) ────────────────────────────────────

  private notify(message: string, type: "success" | "error" | "info" | "warning" = "info"): void {
    (window as any).windowManager?.showNotification(message, type);
  }

  private handleActivateClick(): void {
    if (this.data?.seasonPassOwned) return;

    const socket = window.socket;
    const iapManager = IAPManager.getInstance();
    if (iapManager.getPlatform() === "web") {
      // Desktop has no real IAP flow.
      this.notify(
        i18next.t("ui:instructions.seasonsOnlyMobile") ||
          "Season Premium is only available on the iOS and Android app",
        "info",
      );
      return;
    }
    if (!socket || !iapManager.isAvailable()) {
      this.notify("In-app purchases aren't available here", "error");
      return;
    }

    socket.once("iap-products", (response: { success: boolean; products: SeasonPassProduct[] }) => {
      if (!response.success) {
        this.notify("Failed to load the season pass", "error");
        return;
      }
      // com.extractionzero.season1 (Apple) / com.bubblesquid.extractionzero.season1 (Google)
      // — see server/src/database.ts's iap_products seed.
      const product = response.products.find((p) => p.id === "season1" || p.product_type === "season_pass");
      if (!product) {
        this.notify("Season pass unavailable", "error");
        return;
      }
      this.purchaseSeasonPass(product);
    });
    socket.emit("get-iap-products", { platform: iapManager.getPlatform() === "android" ? "google" : "apple" });
  }

  private async purchaseSeasonPass(product: SeasonPassProduct): Promise<void> {
    const socket = window.socket;
    const iapManager = IAPManager.getInstance();
    if (!socket) return;

    const platform = iapManager.getPlatform();
    const nativeProductId = platform === "android" ? product.google_product_id : product.apple_product_id;
    if (!nativeProductId) {
      this.notify("Season pass unavailable on this platform", "error");
      return;
    }

    // Visual feedback while the native purchase sheet is coming up - without this the
    // button just sits there doing nothing until the store UI appears (or, on iOS, until
    // purchaseProduct's 60s fallback timeout fires - see below).
    this.passButton?.setDisabled(true);
    this.passButton?.setText("...");

    // The native side (iOS SKProductsRequest / Android ProductDetails) can only purchase
    // a product it already has cached from a prior fetch - the shop's buy flow fetches
    // first for the same reason. The season pass is never included in the shop's fetch
    // (see cosmetics-window.ts's skin-only filter), so without this it's never cached and
    // iOS's purchaseProduct silently no-ops until its own 60s fallback timeout fires.
    await iapManager.fetchProducts([nativeProductId]);

    const result = await iapManager.purchaseProduct(nativeProductId);

    this.passButton?.setDisabled(false);
    this.passButton?.setText("ACTIVATE");

    if (!result.success || !result.receipt) {
      if (result.error) this.notify(result.error, "error");
      return;
    }

    const purchaseData: any = {
      productId: product.id,
      platform: platform === "android" ? "google" : "apple",
    };
    if (platform === "android") {
      const androidReceipt = result.receipt as any;
      purchaseData.purchaseToken = androidReceipt.purchaseToken;
      purchaseData.signature = androidReceipt.signature;
      purchaseData.receiptData = androidReceipt.originalJson || "";
    } else {
      purchaseData.appleProductId = result.productId;
      purchaseData.receiptData = result.receipt;
    }

    const onVerified = (response: { success: boolean; error?: string }) => {
      socket.off("iap-purchase-verified", onVerified);

      if (!response.success) {
        this.notify(response.error || "Purchase failed", "error");
        return;
      }

      // Only now safe to finish/acknowledge with the store - the server has
      // confirmed it recorded the purchase.
      if (platform === "android") {
        iapManager.acknowledgePurchase(purchaseData.purchaseToken);
      } else {
        iapManager.confirmTransaction(result.productId!);
      }

      // Refetch and celebrate only once the season data actually comes back owned. A
      // verified receipt alone isn't proof the entitlement landed (see the server's
      // reconcileEntitlement) - confetti over a still-locked track is exactly the
      // failure this guards against.
      this.show(undefined, (data) => {
        if (data?.seasonPassOwned) {
          this.notify("Season Pass activated!", "success");
          showConfetti(this.scene);
          trackEvent("season-pass-activated", { productId: product.id });
        } else {
          this.notify(
            "Purchase went through but the pass didn't activate - try Restore Purchases in the shop",
            "error",
          );
        }
      });
    };
    socket.once("iap-purchase-verified", onVerified);
    socket.emit("iap-purchase", purchaseData);
  }

  // ── Reward track (scrollable) ─────────────────────────────────────────────

  private buildRewardTrack(data: SeasonDataPayload, width: number, track: "free" | "premium", top: number): void {
    const rewards = data.rewards;
    // +1 column of leading space reserved for the level-0 starting marker on the bar.
    const totalContentW = (rewards.length + 1) * CARD_STEP - CARD_GAP + TRACK_PAD * 2;

    const container = this.scene.add.container(this.scrollX, top);
    if (track === "premium") this.premiumContainer = container;
    else this.freeContainer = container;
    this.panel.add(container);

    const innerBg = this.scene.add.graphics();
    innerBg.fillStyle(0x0c0a2e, 0);
    innerBg.fillRect(0, 0, Math.max(this.sidebarW + totalContentW, width), TRACK_H);
    container.add(innerBg);

    const locked = track === "premium" && !data.seasonPassOwned;
    const claimedLevels = new Set(data.claimedSeasonLevels[track]);

    rewards.forEach((row) => {
      // Column = level (not array index) — level L sits at column L, since column 0 is
      // reserved for the level-0 starting marker on the shared progress bar.
      const cardX = this.sidebarW + TRACK_PAD + row.level * CARD_STEP;
      const reward = track === "free" ? row.free : row.premium;
      const isClaimed = claimedLevels.has(row.level);
      // Reached (server-side level) but not yet clicked-to-claim — shown with a distinct
      // border and glowing/clickable, since any earned card can be claimed in any order.
      const isEarned = !locked && !isClaimed && row.level <= data.level;
      const isActive = !locked && row.level === data.level + 1 && !data.isMaxLevel;
      const isGlowing = isEarned;
      const cardKey = this.cardKey(track, row.level);
      // buildCard's content is centered on its own container origin, but cardX/CARD_TOP
      // is expressed as the card's top-left corner (to line up with the level bar/grid) —
      // offset by the card's half-size (scaled) so the visible top-left stays put.
      const card = this.buildCard(
        reward,
        cardX + (CARD_W / 2) * CARD_SCALE,
        CARD_TOP + (CARD_H / 2) * CARD_SCALE,
        isClaimed,
        isActive,
        locked,
        isEarned,
        isGlowing,
        cardKey,
      );
      card.setScale(CARD_SCALE);
      container.add(card);
      this.cardRefs.set(cardKey, card);

      if (isGlowing) {
        this.attachCardGlow(card, track, row.level, data);
      }
    });

    const maskShape = this.scene.add.graphics();
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(0, top, width, TRACK_H);
    maskShape.setVisible(false);
    this.panel.add(maskShape);
    container.setMask(maskShape.createGeometryMask());
  }

  // A single drag zone spanning from the top of the premium track through the level
  // progress bar to the bottom of the free track, so one drag pans both reward rows
  // (and everything visually between them) together instead of scrolling independently.
  private setupUnifiedScroll(width: number): void {
    const top = PREMIUM_TRACK_TOP;
    const zoneH = FREE_TRACK_TOP + TRACK_H - PREMIUM_TRACK_TOP;
    const zone = this.scene.add.zone(0, top, width, zoneH).setOrigin(0, 0).setInteractive();
    this.panel.add(zone);

    zone.on("pointerdown", (p: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragStartX = p.x;
      this.scrollBaseX = this.scrollX;
    });

    this.moveHandler = (p: Phaser.Input.Pointer) => {
      if (!this.isDragging) return;
      const newX = Phaser.Math.Clamp(this.scrollBaseX + (p.x - this.dragStartX), this.scrollMinX, this.scrollMaxX);
      this.scrollX = newX;
      if (this.premiumContainer) this.premiumContainer.x = newX;
      if (this.freeContainer) this.freeContainer.x = newX;
      if (this.levelBarContainer) this.levelBarContainer.x = newX;
      if (this.xpTooltipContainer) this.xpTooltipContainer.x = newX;
    };

    this.upHandler = () => {
      this.isDragging = false;
    };

    this.scene.input.on("pointermove", this.moveHandler);
    this.scene.input.on("pointerup", this.upHandler);
  }

  // ── Xp tooltip show/hide-on-tap ───────────────────────────────────────────────

  private setupXpTooltipInteraction(): void {
    if (this.tapDownHandler) return; // already wired up for this render

    this.tapDownHandler = () => {
      this.showXpTooltip();
    };
    this.tapUpHandler = () => {
      const timer = this.scene.time.delayedCall(4000, () => this.fadeXpTooltip());
      this.activeTimers.push(timer);
    };

    this.scene.input.on("pointerdown", this.tapDownHandler);
    this.scene.input.on("pointerup", this.tapUpHandler);
  }

  private showXpTooltip(): void {
    if (!this.xpTooltipContainer) return;
    this.scene.tweens.killTweensOf(this.xpTooltipContainer);
    this.xpTooltipContainer.setAlpha(1);
  }

  private fadeXpTooltip(): void {
    if (!this.xpTooltipContainer) return;
    const tween = this.scene.tweens.add({
      targets: this.xpTooltipContainer,
      alpha: 0,
      duration: 500,
      ease: "Power2",
    });
    this.activeTweens.push(tween);
  }

  // ── Reward display resolution ─────────────────────────────────────────────

  // Splits a name into a label/sublabel pair for the two-row card layout (see
  // addRewardIcon), the same way loot boxes split their tier name from "BOX" — last
  // word becomes the bigger bottom row, everything before it the smaller top row.
  private splitNameForCard(name: string): { label: string; sublabel: string } {
    const lastSpace = name.lastIndexOf(" ");
    if (lastSpace === -1) return { label: "", sublabel: name.toUpperCase() };
    return {
      label: name.slice(0, lastSpace).toUpperCase(),
      sublabel: name.slice(lastSpace + 1).toUpperCase(),
    };
  }

  private resolveRewardDisplay(reward: SeasonRewardEntry): RewardDisplay {
    const c = this.catalogs!;
    switch (reward.type) {
      case "credits":
        return { label: "CREDITS", isCredits: true, creditsAmount: reward.amount };
      case "loot_box":
        return {
          label: reward.tier.toUpperCase(),
          sublabel: "BOX",
          spriteKey: "lootbox-chests",
          frame: LOOTBOX_FRAME[reward.tier],
          labelColor: LOOTBOX_LABEL_COLOR[reward.tier],
        };
      case "spray": {
        const e = c.sprays.find((x) => x.id === reward.id);
        return { label: i18next.t("ui:labels.spray"), spriteKey: e?.sprite, labelColor: "#ffffff" };
      }
      case "emote": {
        const e = c.emotes.find((x) => x.id === reward.id);
        return {
          label: "EMOTE",
          spriteKey: e ? "emoticons" : undefined,
          frame: e ? emoticonKeyToFrame(e.emoticonKey) : undefined,
          labelColor: "#ffffff",
        };
      }
      case "profile_icon": {
        const e = c.profileIcons.find((x) => x.id === reward.id);
        return { label: i18next.t("ui:labels.icon"), spriteKey: e?.sprite, labelColor: "#ffffff" };
      }
      case "title": {
        return { label: "TITLE", spriteKey: "title-icon", labelColor: "#fbbf24" };
      }
      case "border": {
        const e = c.borders.find((x) => x.id === reward.id);
        const sublabel = (e?.name ?? `Border #${reward.id}`).trim().toUpperCase();
        return { label: "BORDER", sublabel, spriteKey: e?.sprite, labelColor: "#ffffff" };
      }
      case "hat_skin": {
        const e = c.hatSkins.find((x) => x.id === reward.id);
        const label = (e?.name ?? `Hat #${reward.id}`).trim().toUpperCase();
        return { label, sublabel: "HAT", spriteKey: e?.sprite, labelColor: "#ffffff" };
      }
      case "player_skin": {
        const { label, sublabel } = this.splitNameForCard("PLAYER SKIN");
        return { label, sublabel, spriteKey: reward.skinId, labelColor: "#ffffff" };
      }
    }
  }

  // ── Individual reward card ────────────────────────────────────────────────

  private buildCard(
    reward: SeasonRewardEntry,
    x: number,
    y: number,
    claimed: boolean,
    active: boolean,
    locked: boolean,
    earned: boolean = false,
    glowing: boolean = false,
    pulseKey?: string,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    this.populateCard(c, reward, claimed, active, locked, earned, glowing, pulseKey);
    return c;
  }

  // Split out from buildCard so a claim click (or a glow handoff) can redraw one card's
  // contents in place (new border/badge/wash) without rebuilding its container — that's
  // what keeps its scale, position, and cardRefs entry stable.
  private populateCard(
    c: Phaser.GameObjects.Container,
    reward: SeasonRewardEntry,
    claimed: boolean,
    active: boolean,
    locked: boolean,
    earned: boolean,
    glowing: boolean,
    pulseKey?: string,
  ): void {
    // Drop any pulse tween this card already owned (e.g. it's being claimed right now)
    // before removeAll(true) destroys its graphics out from under the tween.
    if (pulseKey) this.removeCardPulse(pulseKey);
    c.removeAll(true);

    // Gold border is reserved for earned-unclaimed cards — the "you have this waiting"
    // cue, matching the level bar's "claimed" dot color. Active (next-up, not yet
    // reached) keeps a distinct fill tone but the same gray border as a plain/locked
    // card, since it hasn't actually been earned.
    const borderColor = claimed ? 0x22c55e : earned ? 0xeab308 : 0x3b4a6b;
    const fillColor = active ? 0x1e293b : claimed ? 0x14532d : earned ? 0x422006 : 0x111827;

    const bg = this.scene.add.graphics();
    bg.fillStyle(fillColor, 0.5);
    bg.fillRoundedRect(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H, 8);
    c.add(bg);

    // Every earned, unclaimed card pulsates — they're all glowing/clickable now.
    if (glowing) this.attachCardPulse(c, pulseKey);

    // Every card gets a couple of diagonal glass-shine streaks over its background.
    this.addGlassSheen(c);

    // Border drawn as its own graphics object, added after the wash/sheen, so it renders
    // in front of the (possibly opaque) white background instead of being covered by it.
    const border = this.scene.add.graphics();
    border.lineStyle(6, 0x000000, 1);
    border.strokeRoundedRect(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H, 8);
    border.lineStyle(earned ? 3 : 2, borderColor, 1);
    border.strokeRoundedRect(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H, 8);
    c.add(border);

    const display = this.resolveRewardDisplay(reward);
    if (display.isCredits) {
      this.addCreditsContent(c, display.creditsAmount ?? 0);
    } else {
      this.addRewardIcon(c, display);
    }

    if (claimed) this.addClaimedBadge(c);

    if (locked) {
      // Outline traced around the icon's own silhouette: stacked black-tinted copies
      // offset a couple pixels in every direction, drawn behind the real icon.
      const outlineOffsets = [
        [-2, 0], [2, 0], [0, -2], [0, 2],
        [-2, -2], [2, -2], [-2, 2], [2, 2],
      ];
      for (const [dx, dy] of outlineOffsets) {
        const outline = this.scene.add.image(dx, dy, "lock-icon");
        outline.setDisplaySize(56, 56);
        outline.setTint(0x000000);
        c.add(outline);
      }

      const lockIcon = this.scene.add.image(0, 0, "lock-icon");
      lockIcon.setDisplaySize(56, 56);
      c.add(lockIcon);
    }
  }

  private addCreditsContent(c: Phaser.GameObjects.Container, quantity: number): void {
    const icon = this.scene.add.sprite(0, -20, "weekly-rewards", getCreditsIconFrame(quantity));
    icon.setDisplaySize(144, 144);
    c.add(icon);

    c.add(
      this.scene.add
        .text(0, 74, String(quantity), {
          fontSize: "36px",
          fontFamily: "Anton, sans-serif",
          fontStyle: "bold",
          color: "#fbbf24",
          stroke: "#000000",
          strokeThickness: 4,
        })
        .setOrigin(0.5),
    );

    c.add(
      this.scene.add
        .text(0, 44, "CREDITS", {
          fontSize: "26px",
          fontFamily: "Anton, sans-serif",
          color: "#fbbf24",
          letterSpacing: 1,
          stroke: "#000000",
          strokeThickness: 4,
        })
        .setOrigin(0.5),
    );
  }

  private addRewardIcon(c: Phaser.GameObjects.Container, display: RewardDisplay): void {
    if (display.spriteKey && this.scene.textures.exists(display.spriteKey)) {
      const sprite = this.scene.add.sprite(0, -18, display.spriteKey, display.frame ?? 0);
      sprite.setDisplaySize(96, 96);
      c.add(sprite);
    } else {
      this.addItemPlaceholder(c);
    }

    if (display.labelColor) {
      if (display.sublabel) {
        c.add(
          this.scene.add
            .text(0, 44, display.label, {
              fontSize: "22px",
              fontFamily: "Anton, sans-serif",
              color: display.labelColor,
              letterSpacing: 1,
              stroke: "#000000",
              strokeThickness: 6,
            })
            .setOrigin(0.5),
        );
        c.add(
          this.scene.add
            .text(0, 72, display.sublabel, {
              fontSize: "36px",
              fontFamily: "Anton, sans-serif",
              color: display.labelColor,
              letterSpacing: 1,
              stroke: "#000000",
              strokeThickness: 8,
            })
            .setOrigin(0.5),
        );
      } else {
        // No sublabel (e.g. emotes) — a single line, styled/positioned like the
        // bigger "BOX" row instead of the smaller tier-name row.
        c.add(
          this.scene.add
            .text(0, 72, display.label, {
              fontSize: "36px",
              fontFamily: "Anton, sans-serif",
              color: display.labelColor,
              letterSpacing: 1,
              stroke: "#000000",
              strokeThickness: 8,
            })
            .setOrigin(0.5),
        );
      }
    } else {
      c.add(
        this.scene.add
          .text(0, 40, display.label, {
            fontSize: "10px",
            fontFamily: "Arial",
            color: "#e2e8f0",
            align: "center",
            wordWrap: { width: CARD_W - 10 },
            stroke: "#000000",
            strokeThickness: 2,
          })
          .setOrigin(0.5),
      );
    }
  }

  private addItemPlaceholder(c: Phaser.GameObjects.Container): void {
    const ph = this.scene.add.graphics();
    ph.fillStyle(0x374151, 1);
    ph.fillRoundedRect(-22, -40, 44, 44, 6);
    c.add(ph);

    c.add(
      this.scene.add
        .text(0, -18, "?", {
          fontSize: "26px",
          fontFamily: "Arial Black, Arial",
          fontStyle: "bold",
          color: "#6b7280",
        })
        .setOrigin(0.5),
    );
  }

  private addClaimedBadge(c: Phaser.GameObjects.Container): void {
    const circle = this.scene.add.graphics();
    circle.fillStyle(0x16a34a, 1);
    circle.fillCircle(CARD_W / 2 - 13, -CARD_H / 2 + 13, 20);
    c.add(circle);

    c.add(
      this.scene.add
        .text(CARD_W / 2 - 13, -CARD_H / 2 + 13, "✓", {
          fontSize: "24px",
          fontFamily: "Arial",
          fontStyle: "bold",
          color: "#ffffff",
        })
        .setOrigin(0.5),
    );
  }

  // ── Card claiming (earned → glow → click → claimed) ──────────────────────────

  private cardKey(track: "free" | "premium", level: number): string {
    return `${track}-${level}`;
  }

  // Bakes the glass-sheen streaks into a small shared texture, once, using the native
  // Canvas 2D clip() to crop them to a plain CARD_W x CARD_H rect — Phaser's GeometryMask
  // consistently failed here (either hid the streaks entirely or didn't clip them at
  // all, across a couple of different setups), so this sidesteps Phaser's mask system
  // altogether: the clipping happens once, in a plain 2D context, before Phaser ever
  // sees the result, and every card just stamps the same pre-clipped image.
  private ensureGlassSheenTexture(): void {
    const key = "season-card-sheen";
    if (this.scene.textures.exists(key)) return;

    const canvasTexture = this.scene.textures.createCanvas(key, CARD_W, CARD_H);
    if (!canvasTexture) return;
    const ctx = canvasTexture.context;

    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, CARD_W, CARD_H);
    ctx.clip();

    const run = 120; // equal horizontal/vertical span, i.e. a true 45° line
    const drawStreak = (xBottom: number, yBottom: number, width: number, alpha: number) => {
      const xTop = xBottom + run;
      const yTop = yBottom - run;
      ctx.lineWidth = width;
      ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
      ctx.beginPath();
      ctx.moveTo(xBottom, yBottom);
      ctx.lineTo(xTop, yTop);
      ctx.stroke();
    };
    drawStreak(-10, 70, 40, 0.22); // thicker streak
    drawStreak(-10, 150, 20, 0.22); // thinner streak

    ctx.restore();
    canvasTexture.update();
  }

  // Two diagonal semi-transparent white streaks (one thicker, one thinner) across every
  // card, giving its background a glass-like sheen — see ensureGlassSheenTexture for how
  // they're actually clipped to the card's bounds.
  private addGlassSheen(c: Phaser.GameObjects.Container): void {
    this.ensureGlassSheenTexture();
    const sheen = this.scene.add.image(0, 0, "season-card-sheen").setOrigin(0.5, 0.5);
    c.add(sheen);
  }

  // Wash over the card's own background, shifting between the earned border's gold and
  // white continuously — every currently-glowing/clickable (earned, unclaimed) card gets
  // one of these. Sits above bg but below the icon/text so the reward stays readable
  // through it.
  //
  // Graphics has no tweenable "color" property, so a plain state object's 0..1 progress
  // is tweened instead, and onUpdate blends and redraws the fill each step.
  private attachCardPulse(c: Phaser.GameObjects.Container, pulseKey?: string): void {
    const pulse = this.scene.add.graphics();
    c.add(pulse);

    const gold = Phaser.Display.Color.ValueToColor(0xeab308);
    const white = Phaser.Display.Color.ValueToColor(0xffffff);
    const state = { t: 0 };
    const redraw = () => {
      const blended = Phaser.Display.Color.Interpolate.ColorWithColor(gold, white, 100, state.t * 100);
      pulse.clear();
      pulse.fillStyle(Phaser.Display.Color.GetColor(blended.r, blended.g, blended.b), 1);
      pulse.fillRoundedRect(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H, 8);
    };
    redraw();

    const tween = this.scene.tweens.add({
      targets: state,
      t: { from: 0, to: 1 },
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
      onUpdate: redraw,
    });
    this.activeTweens.push(tween);
    if (pulseKey) this.cardPulses.set(pulseKey, { tween, gfx: pulse });
  }

  private removeCardPulse(pulseKey: string): void {
    const pulse = this.cardPulses.get(pulseKey);
    if (!pulse) return;
    pulse.tween.stop();
    pulse.gfx.destroy();
    this.cardPulses.delete(pulseKey);
  }

  // Lazily bakes the two particle textures the card-glow fire effect uses — a small
  // white and yellow dot, generated once and cached like sound-utils.ts's glow indicator.
  private ensureFireParticleTextures(): void {
    if (this.scene.textures.exists("season-fire-yellow")) return;
    const g = this.scene.add.graphics();
    g.fillStyle(0xfacc15, 1);
    g.fillCircle(4, 4, 4);
    g.generateTexture("season-fire-yellow", 8, 8);
    g.clear();
    g.fillStyle(0xffffff, 1);
    g.fillCircle(4, 4, 4);
    g.generateTexture("season-fire-white", 8, 8);
    g.destroy();
  }

  // Flame rising all the way around the card's outline — spawn points are scattered
  // along the perimeter of the card rect (an "edge" emit zone) rather than a single
  // fixed point, with a yellow outer emitter plus a narrower white core, both
  // continuously emitting upward (negative gravityY, ADD blend) — the same "rising
  // beam" convention as splash-particle.ts's showBeamUpParticle, spread around the card.
  private createCardFireEmitters(card: Phaser.GameObjects.Container): Phaser.GameObjects.Particles.ParticleEmitter[] {
    this.ensureFireParticleTextures();
    // Inset slightly so spawn points sit on the card's border stroke, not its outer edge.
    const perimeter = new Phaser.Geom.Rectangle(-CARD_W / 2 + 2, -CARD_H / 2 + 2, CARD_W - 4, CARD_H - 4);

    const yellowEmitter = this.scene.add.particles(0, 0, "season-fire-yellow", {
      emitZone: { type: "edge", source: perimeter, quantity: 48 },
      lifespan: { min: 450, max: 700 },
      speed: { min: 20, max: 50 },
      angle: { min: -100, max: -80 },
      // Eased scale/alpha instead of a linear ramp, plus a gentle *positive* gravity so
      // the rise decelerates smoothly over its lifespan (real flame slows as it cools)
      // rather than continuously speeding up in a straight line.
      scale: { start: 0.9, end: 0, ease: "Sine.easeOut" },
      alpha: { start: 0.9, end: 0, ease: "Sine.easeIn" },
      frequency: 25,
      quantity: 1,
      blendMode: Phaser.BlendModes.ADD,
      gravityY: 30,
    });

    const whiteEmitter = this.scene.add.particles(0, 0, "season-fire-white", {
      emitZone: { type: "edge", source: perimeter, quantity: 48 },
      lifespan: { min: 300, max: 480 },
      speed: { min: 15, max: 35 },
      angle: { min: -100, max: -80 },
      scale: { start: 0.55, end: 0, ease: "Sine.easeOut" },
      alpha: { start: 0.85, end: 0, ease: "Sine.easeIn" },
      frequency: 35,
      quantity: 1,
      blendMode: Phaser.BlendModes.ADD,
      gravityY: 20,
    });

    // Added on top (not addAt(0)) so the flame licks up in front of the card face and
    // above its top edge, instead of hiding behind the background fill.
    card.add(yellowEmitter);
    card.add(whiteEmitter);
    return [yellowEmitter, whiteEmitter];
  }

  // Marks a card as claimable: gives it a rising fire-particle glow and makes it
  // clickable. Every earned, unclaimed card in a track gets one of these simultaneously —
  // the player can claim them in any order.
  private attachCardGlow(
    card: Phaser.GameObjects.Container,
    track: "free" | "premium",
    level: number,
    data: SeasonDataPayload,
  ): void {
    const key = this.cardKey(track, level);
    if (this.cardGlows.has(key)) return;

    this.cardGlows.set(key, this.createCardFireEmitters(card));

    // Down-arms/up-fires, mirroring ModernButton's click convention, so a drag that
    // happens to start on the card doesn't register as a claim.
    let pressed = false;
    card.setInteractive(
      new Phaser.Geom.Rectangle(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H),
      Phaser.Geom.Rectangle.Contains,
    );
    card.on("pointerover", () => this.scene.input.setDefaultCursor("pointer"));
    card.on("pointerout", () => {
      this.scene.input.setDefaultCursor("default");
      pressed = false;
    });
    card.on("pointerdown", () => {
      pressed = true;
    });
    card.on("pointerup", () => {
      if (!pressed) return;
      pressed = false;
      this.claimReward(data, track, level, card);
    });
  }

  private removeCardGlow(track: "free" | "premium", level: number): void {
    const key = this.cardKey(track, level);
    const emitters = this.cardGlows.get(key);
    if (!emitters) return;
    emitters.forEach((e) => e.destroy());
    this.cardGlows.delete(key);
  }

  // Asks the server to actually grant this level's reward (the only place it's granted —
  // see SeasonManager.claimReward) before running the reveal (reusing playRewardReveal
  // with a one-item list, so loot boxes still get their open/wiggle/confetti flow) and
  // redrawing the card in place. Every other earned card keeps its own glow untouched.
  private claimReward(
    data: SeasonDataPayload,
    track: "free" | "premium",
    level: number,
    card: Phaser.GameObjects.Container,
  ): void {
    this.removeCardGlow(track, level);
    card.disableInteractive();
    this.scene.input.setDefaultCursor("default");

    const row = data.rewards.find((r) => r.level === level);
    if (!row) return;
    const reward = track === "free" ? row.free : row.premium;

    const socket = (window as any).socket;
    if (!socket) {
      this.attachCardGlow(card, track, level, data);
      return;
    }

    let settled = false;
    const responseHandler = (response: {
      success: boolean;
      track: "free" | "premium";
      level: number;
      error?: string;
      playerData?: Record<string, unknown>;
    }) => {
      if (response.track !== track || response.level !== level) return;
      settled = true;
      socket.off("season-reward-claimed", responseHandler);

      if (!response.success) {
        console.error("[SeasonScreen] Failed to claim reward:", response.error);
        this.attachCardGlow(card, track, level, data);
        return;
      }

      if (response.playerData && (window as any).player) {
        (window as any).player.data = {
          ...(window as any).player.data,
          ...response.playerData,
        };
      }

      trackEvent("season-reward-claimed", { track, level });
      this.playRewardReveal([{ level, track, reward }], this.screenWidth, this.screenHeight, () => {
        data.claimedSeasonLevels[track].push(level);
        this.populateCard(card, reward, true, false, false, false, false, this.cardKey(track, level));
      });
    };

    socket.on("season-reward-claimed", responseHandler);
    socket.emit("claim-season-reward", { track, level });

    const timeout = this.scene.time.delayedCall(5000, () => {
      if (settled) return;
      socket.off("season-reward-claimed", responseHandler);
      console.warn("[SeasonScreen] Claim reward timeout");
      this.attachCardGlow(card, track, level, data);
    });
    this.activeTimers.push(timeout);
  }

  // Pop/flash feedback played once when the bar-fill animation crosses a new level (see
  // renderMain's playStop) — purely cosmetic, independent of claim state.

  private popDot(level: number): void {
    if (!this.levelBarGeom) return;
    const dot = this.levelBarGeom.container.getByName(`level-dot-${level}`) as Phaser.GameObjects.Graphics | null;
    if (!dot) return;
    dot.setScale(0.35);
    const tween = this.scene.tweens.add({
      targets: dot,
      scale: 1,
      duration: 420,
      ease: "Back.easeOut",
    });
    this.activeTweens.push(tween);
  }

  private flashLevelCards(level: number): void {
    (["free", "premium"] as const).forEach((track) => {
      const card = this.cardRefs.get(this.cardKey(track, level));
      if (card) this.flashCard(card);
    });
  }

  private flashCard(card: Phaser.GameObjects.Container): void {
    const flash = this.scene.add.graphics();
    flash.fillStyle(0xffffff, 0.85);
    flash.fillRoundedRect(-CARD_W / 2, -CARD_H / 2, CARD_W, CARD_H, 8);
    card.add(flash);

    const punch = this.scene.tweens.add({
      targets: card,
      scaleX: card.scaleX * 1.18,
      scaleY: card.scaleY * 1.18,
      duration: 140,
      yoyo: true,
      repeat: 1,
      ease: "Sine.easeInOut",
    });
    this.activeTweens.push(punch);

    const fade = this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 500,
      ease: "Cubic.easeOut",
      onComplete: () => flash.destroy(),
    });
    this.activeTweens.push(fade);
  }

  // ── Level progress bar (shared, between the two tracks) ─────────────────────
  // Shows every level as a dot connected by a line (scrolls in sync with the reward
  // tracks, aligned under the same card columns), starting from an explicit "0" dot —
  // you haven't earned anything until you reach level 1 — through to the max level;
  // total season xp progress is drawn as a partial fill on the line segment leading
  // into the next (active) dot, including the very first 0-to-1 segment.

  private buildLevelProgressBar(data: SeasonDataPayload, width: number, top: number, startXp?: number): void {
    const totalLevels = data.rewards.length;
    // +1 column of leading space reserved for the level-0 starting marker.
    const totalContentW = (totalLevels + 1) * CARD_STEP - CARD_GAP + TRACK_PAD * 2;

    const container = this.scene.add.container(this.scrollX, top);
    this.levelBarContainer = container;
    this.panel.add(container);

    // Separate, unmasked container for the xp tooltip so it can extend above the level
    // bar's row (which is otherwise clipped to LEVEL_BAR_H) — still scrolled in sync.
    const tooltipContainer = this.scene.add.container(this.scrollX, top);
    this.xpTooltipContainer = tooltipContainer;
    this.panel.add(tooltipContainer);

    const innerBg = this.scene.add.graphics();
    innerBg.fillStyle(0x0c0a2e, 0);
    innerBg.fillRect(0, 0, Math.max(this.sidebarW + totalContentW, width), LEVEL_BAR_H);
    container.add(innerBg);

    const cy = LEVEL_BAR_CENTER_Y;
    // firstX is the level-0 dot (column 0); level L's dot sits at firstX + L * CARD_STEP.
    const firstX = this.sidebarW + TRACK_PAD + CARD_DISP_W / 2;
    const lastX = this.sidebarW + TRACK_PAD + totalLevels * CARD_STEP + CARD_DISP_W / 2;
    const barW = lastX - firstX;
    const segFrac = totalLevels > 0 ? 1 / totalLevels : 0;

    const maskShape = this.scene.add.graphics();
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(0, top, width, LEVEL_BAR_H);
    maskShape.setVisible(false);
    this.panel.add(maskShape);
    container.setMask(maskShape.createGeometryMask());

    this.levelBarGeom = { container, tooltipContainer, totalLevels, firstX, barW, segFrac, cy };
    this.drawProgressFill(startXp ?? data.seasonXp, startXp === undefined);
  }

  // Redraws everything in the level bar that depends on progress (dot borders/fills,
  // labels, the claimed/xp fill rects, and — only when showTooltip — the "xp left"
  // tooltip), parameterized purely by an xp value. Called once for a static render,
  // and repeatedly with showTooltip=false from a tweens.addCounter while animating
  // the bar filling up from a match's pre-match xp to its post-match xp.
  private drawProgressFill(displayXp: number, showTooltip: boolean = false): void {
    if (!this.levelBarGeom) return;
    const { container, tooltipContainer, totalLevels, firstX, barW, segFrac, cy } = this.levelBarGeom;

    this.levelBarDynamicObjs.forEach((o) => o.destroy());
    this.levelBarDynamicObjs = [];
    tooltipContainer.removeAll(true);

    const progress = getSeasonProgress(displayXp);
    const claimed = progress.level;

    // Dot borders are drawn first so the bar's track/fill render on top of them —
    // only the un-covered rim of each border (poking out past the bar) stays visible.
    // dotIdx runs 0..totalLevels: dotIdx 0 is the "level 0" starting marker (always
    // shown as reached, since it costs no xp), dotIdx 1..totalLevels are the real
    // reward levels.
    for (let dotIdx = 0; dotIdx <= totalLevels; dotIdx++) {
      const dotX = firstX + dotIdx * CARD_STEP;
      const isClaimed = dotIdx <= claimed;
      const isActive = dotIdx === claimed + 1;
      const radius = isActive || isClaimed ? LEVEL_DOT_R + 2 : LEVEL_DOT_R;

      const border = this.scene.add.graphics();
      border.fillStyle(0x000000, 1);
      border.fillCircle(dotX, cy, radius + LEVEL_DOT_BORDER_W);
      container.add(border);
      this.levelBarDynamicObjs.push(border);
    }

    // Padded so the track (and its border) fully enclose the first/last dots, not just
    // run between their centers.
    const barPad = LEVEL_DOT_R + 2;
    const barBg = this.scene.add.graphics();
    barBg.fillStyle(0x374151, 1);
    barBg.fillRoundedRect(firstX - barPad, cy - LEVEL_BAR_LINE_H / 2, barW + barPad * 2, LEVEL_BAR_LINE_H, LEVEL_BAR_LINE_H / 2);
    barBg.lineStyle(2, 0x000000, 1);
    barBg.strokeRoundedRect(firstX - barPad, cy - LEVEL_BAR_LINE_H / 2, barW + barPad * 2, LEVEL_BAR_LINE_H, LEVEL_BAR_LINE_H / 2);
    container.add(barBg);
    this.levelBarDynamicObjs.push(barBg);

    // claimed/totalLevels of the totalLevels segments (0→1, 1→2, ... ) are fully complete.
    const fillFrac = totalLevels > 0 ? Math.min(1, claimed / totalLevels) : 0;
    if (fillFrac > 0) {
      const fill = this.scene.add.graphics();
      fill.fillStyle(0x22c55e, 1);
      fill.fillRoundedRect(firstX, cy - LEVEL_BAR_FILL_H / 2, barW * fillFrac, LEVEL_BAR_FILL_H, LEVEL_BAR_FILL_H / 2);
      container.add(fill);
      this.levelBarDynamicObjs.push(fill);
    }

    // Includes the claimed===0 case so xp gained toward level 1 (before it's reached)
    // fills the very first 0→1 segment instead of showing nothing.
    const xpFrac = progress.isMaxLevel ? 0 : progress.xpForNextLevel > 0 ? progress.xpIntoLevel / progress.xpForNextLevel : 0;
    if (xpFrac > 0 && claimed >= 0 && claimed < totalLevels) {
      const progressFill = this.scene.add.graphics();
      progressFill.fillStyle(0xfbbf24, 1);
      progressFill.fillRoundedRect(
        firstX + barW * fillFrac,
        cy - LEVEL_BAR_FILL_H / 2,
        barW * segFrac * xpFrac,
        LEVEL_BAR_FILL_H,
        LEVEL_BAR_FILL_H / 2,
      );
      container.add(progressFill);
      this.levelBarDynamicObjs.push(progressFill);
    }

    for (let dotIdx = 0; dotIdx <= totalLevels; dotIdx++) {
      const dotX = firstX + dotIdx * CARD_STEP;
      const isClaimed = dotIdx <= claimed;
      const isActive = dotIdx === claimed + 1;
      const radius = isActive || isClaimed ? LEVEL_DOT_R + 2 : LEVEL_DOT_R;

      // Positioned at (dotX, cy) with the circle drawn at its own local origin — unlike the
      // other graphics in this function — so popDot() can scale it around its true center
      // instead of around (0, 0).
      const dot = this.scene.add.graphics();
      dot.setPosition(dotX, cy);
      dot.fillStyle(isClaimed ? 0xeab308 : isActive ? 0xffffff : 0x4b5563, 1);
      dot.fillCircle(0, 0, radius);
      if (isActive) {
        dot.lineStyle(2, 0xffffff, 0.6);
        dot.strokeCircle(0, 0, radius + 2);
      }
      dot.name = `level-dot-${dotIdx}`;
      container.add(dot);
      this.levelBarDynamicObjs.push(dot);

      const label = this.scene.add
        .text(dotX, cy + radius + 3, String(dotIdx), {
          fontSize: "22px",
          fontFamily: "Anton, sans-serif",
          color: isClaimed ? "#22c55e" : isActive ? "#fbbf24" : "#6b7280",
          stroke: "#000000",
          strokeThickness: 3,
        })
        .setOrigin(0.5, 0);
      container.add(label);
      this.levelBarDynamicObjs.push(label);
    }

    // Tooltip showing xp left for the next level: floats above the fill with a
    // downward-pointing arrow centered on it. Visible for 4s then fades out; tapping
    // the screen brings it back (see setupXpTooltipInteraction). Skipped on intermediate
    // animation frames (showTooltip=false) so it only ever appears once the bar settles.
    if (showTooltip && !progress.isMaxLevel && progress.xpForNextLevel > 0 && claimed >= 0 && claimed < totalLevels) {
      const xpLeft = progress.xpForNextLevel - progress.xpIntoLevel;
      const fillEndX = firstX + barW * fillFrac + barW * segFrac * xpFrac;

      const arrowSize = 8;
      const arrowH = 10;
      const tooltipW = 100;
      const tooltipH = 56;
      const arrowTipY = cy - 8;
      const arrowBaseY = arrowTipY - arrowH;
      const boxBottomY = arrowBaseY;
      const boxTopY = boxBottomY - tooltipH;
      const boxCenterY = boxTopY + tooltipH / 2;

      // Box (rounded rect) + arrow (triangle) drawn separately so the box gets rounded
      // corners — only the arrow's two slanted edges are stroked, since its top edge
      // sits flush against the box's flat bottom and is already covered by both fills.
      const boxLeft = fillEndX - tooltipW / 2;
      const boxRadius = 8;

      const tooltipBg = this.scene.add.graphics();
      tooltipBg.fillStyle(0x1e1b4b, 1);
      tooltipBg.fillRoundedRect(boxLeft, boxTopY, tooltipW, tooltipH, boxRadius);
      tooltipBg.fillTriangle(
        fillEndX - arrowSize, boxBottomY,
        fillEndX + arrowSize, boxBottomY,
        fillEndX, arrowTipY,
      );
      tooltipBg.lineStyle(2, 0xfbbf24, 1);
      tooltipBg.strokeRoundedRect(boxLeft, boxTopY, tooltipW, tooltipH, boxRadius);
      tooltipBg.beginPath();
      tooltipBg.moveTo(fillEndX - arrowSize, boxBottomY);
      tooltipBg.lineTo(fillEndX, arrowTipY);
      tooltipBg.lineTo(fillEndX + arrowSize, boxBottomY);
      tooltipBg.strokePath();
      tooltipContainer.add(tooltipBg);

      tooltipContainer.add(
        this.scene.add
          .text(fillEndX, boxCenterY, `${xpLeft.toLocaleString()} XP LEFT\nTO LEVEL ${claimed + 1}`, {
            fontSize: "12px",
            fontFamily: "Anton, sans-serif",
            color: "#ffffff",
            align: "center",
            lineSpacing: 4,
          })
          .setOrigin(0.5),
      );

      tooltipContainer.setAlpha(1);
      const hideTimer = this.scene.time.delayedCall(4000, () => this.fadeXpTooltip());
      this.activeTimers.push(hideTimer);
      this.setupXpTooltipInteraction();
    }
  }

  // ── Reward-reveal animation (client-local; see plan for rationale) ──────────

  private playRewardReveal(reveals: PendingReveal[], width: number, height: number, onComplete: () => void): void {
    let index = 0;
    let boxOpened = false;
    let isOpening = false;
    let currentCard: Phaser.GameObjects.Container | null = null;

    const blocker = this.scene.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.75);
    blocker.setInteractive();
    this.panel.add(blocker);

    const contentContainer = this.scene.add.container(width / 2, height / 2);
    this.panel.add(contentContainer);

    const nextButton = new ModernButton({
      scene: this.scene,
      x: width / 2,
      y: height / 2 + CARD_H / 2 + 60,
      text: "NEXT",
      style: "primary",
      onClick: () => {
        if (isOpening) return;
        const reveal = reveals[index];
        if (reveal.reward.type === "loot_box" && !boxOpened) openBox();
        else advance();
      },
    });
    this.panel.add(nextButton);

    const updateButtonLabel = () => {
      const reveal = reveals[index];
      if (reveal.reward.type === "loot_box" && !boxOpened) {
        nextButton.setText("OPEN");
      } else {
        nextButton.setText(index === reveals.length - 1 ? "CLAIM" : "NEXT");
      }
    };

    const showCurrent = () => {
      contentContainer.removeAll(true);
      boxOpened = false;
      isOpening = false;
      const reveal = reveals[index];

      const levelText = this.scene.add
        .text(0, -CARD_H / 2 - 50, `LEVEL ${reveal.level}`, {
          fontSize: "22px",
          fontFamily: "Arial Black, Arial",
          fontStyle: "bold",
          color: "#fbbf24",
        })
        .setOrigin(0.5)
        .setScale(0);
      contentContainer.add(levelText);

      const trackText = this.scene.add
        .text(0, -CARD_H / 2 - 22, reveal.track === "free" ? "FREE REWARD" : "PREMIUM REWARD", {
          fontSize: "12px",
          fontFamily: "Anton, sans-serif",
          color: "#e2e8f0",
        })
        .setOrigin(0.5);
      contentContainer.add(trackText);

      const card = this.buildCard(reveal.reward, 0, 0, false, false, false);
      card.setScale(0);
      contentContainer.add(card);
      currentCard = card;

      const t1 = this.scene.tweens.add({
        targets: [levelText, card],
        scale: 1,
        duration: 500,
        delay: 150,
        ease: "Elastic.out",
      });
      this.activeTweens.push(t1);

      updateButtonLabel();
    };

    // Loot boxes don't auto-reveal their contents — the player must tap OPEN first,
    // which plays a wiggle on the chest card then animates in the 3 rolled items.
    const openBox = () => {
      const reveal = reveals[index];
      if (reveal.reward.type !== "loot_box" || boxOpened || isOpening || !currentCard) return;
      isOpening = true;
      const card = currentCard;

      const wiggle = this.scene.tweens.add({
        targets: card,
        angle: { from: -4, to: 4 },
        duration: 150,
        yoyo: true,
        repeat: 2,
        ease: "Sine.easeInOut",
        onComplete: () => {
          showConfetti(this.scene);
          // Hide the chest rather than leaving it on screen — the items take its place.
          const hideChest = this.scene.tweens.add({
            targets: card,
            scale: 0,
            alpha: 0,
            duration: 200,
            ease: "Back.easeIn",
          });
          this.activeTweens.push(hideChest);
          this.revealLootBoxItems(reveal, contentContainer);
          boxOpened = true;
          isOpening = false;
          updateButtonLabel();
        },
      });
      this.activeTweens.push(wiggle);
    };

    const advance = () => {
      index++;
      if (index >= reveals.length) {
        blocker.destroy();
        contentContainer.destroy();
        nextButton.destroy();
        onComplete();
        return;
      }
      showCurrent();
    };

    showCurrent();
  }

  // Loot box contents are now fixed/explicit (SeasonRewardEntry's "loot_box" items[]
  // in seasons.json), not rolled — so this just lays out reveal.reward.items straight
  // from the reward itself, wrapping into multiple rows once a box holds more than
  // ITEMS_PER_ROW distinct items, with a "x<qty>" badge on any stack > 1.
  private revealLootBoxItems(reveal: PendingReveal, contentContainer: Phaser.GameObjects.Container): void {
    if (reveal.reward.type !== "loot_box") return;
    const itemDefs = this.catalogs?.items ?? [];
    const entries = reveal.reward.items
      .map((entry) => ({ def: itemDefs.find((d) => d.id === entry.id), qty: entry.qty }))
      .filter((e): e is { def: ItemDefinition; qty: number } => !!e.def);

    if (entries.length === 0) {
      // Definitions failed to resolve against the catalog — items were still granted
      // server-side, just nothing to render.
      const fallback = this.scene.add
        .text(0, 10, "Items added to storage", {
          fontSize: "12px",
          fontFamily: "Arial",
          color: "#94a3b8",
        })
        .setOrigin(0.5)
        .setAlpha(0);
      contentContainer.add(fallback);
      const t = this.scene.tweens.add({ targets: fallback, alpha: 1, duration: 300 });
      this.activeTweens.push(t);
      return;
    }

    const ITEM_STEP_X = 84;
    const ITEM_STEP_Y = 112;
    const ITEMS_PER_ROW = 3;
    const rows = Math.ceil(entries.length / ITEMS_PER_ROW);
    const startY = -((rows - 1) * ITEM_STEP_Y) / 2;

    entries.forEach(({ def, qty }, i) => {
      const row = Math.floor(i / ITEMS_PER_ROW);
      const col = i % ITEMS_PER_ROW;
      const itemsInRow = Math.min(ITEMS_PER_ROW, entries.length - row * ITEMS_PER_ROW);
      const rowStartX = -((itemsInRow - 1) * ITEM_STEP_X) / 2;

      const card = this.buildItemRevealCard(def, qty, rowStartX + col * ITEM_STEP_X, startY + row * ITEM_STEP_Y);
      card.setScale(0);
      contentContainer.add(card);
      const t = this.scene.tweens.add({
        targets: card,
        scale: 1,
        duration: 400,
        delay: i * 120,
        ease: "Elastic.out",
      });
      this.activeTweens.push(t);
    });
  }

  private buildItemRevealCard(def: ItemDefinition, qty: number, x: number, y: number): Phaser.GameObjects.Container {
    const w = 72;
    const h = 100;
    const color = getItemRarityColor(def.price);
    const c = this.scene.add.container(x, y);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x111827, 0.9);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 8);
    bg.lineStyle(5, 0x000000, 1);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);
    bg.lineStyle(2, color, 1);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 8);
    c.add(bg);

    if (this.scene.textures.exists(def.spriteSheet)) {
      const sprite = SpriteLoader.createSprite(this.scene, def.spriteSheet, def.spriteX, def.spriteY, 0, -14, true);
      c.add(sprite);
    }

    // Label sits below the sprite with room for two rows before it can grow bigger
    // still — top-anchored so a wrapped second line grows downward instead of
    // pushing the first line up into the sprite.
    c.add(
      this.scene.add
        .text(0, h / 2 - 34, def.name, {
          fontSize: "16px",
          fontFamily: "Anton, sans-serif",
          color: "#e2e8f0",
          align: "center",
          wordWrap: { width: w - 6 },
          lineSpacing: 2,
          stroke: "#000000",
          strokeThickness: 2,
        })
        .setOrigin(0.5, 0),
    );

    if (qty > 1) {
      c.add(
        this.scene.add
          .text(w / 2 - 4, -h / 2 + 4, `x${qty}`, {
            fontSize: "14px",
            fontFamily: "Anton, sans-serif",
            color: "#fbbf24",
            stroke: "#000000",
            strokeThickness: 4,
          })
          .setOrigin(1, 0),
      );
    }

    return c;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────────

  private cleanupScrollHandlers(): void {
    if (this.moveHandler) {
      this.scene.input.off("pointermove", this.moveHandler);
      this.moveHandler = null;
    }
    if (this.upHandler) {
      this.scene.input.off("pointerup", this.upHandler);
      this.upHandler = null;
    }
    if (this.tapDownHandler) {
      this.scene.input.off("pointerdown", this.tapDownHandler);
      this.tapDownHandler = null;
    }
    if (this.tapUpHandler) {
      this.scene.input.off("pointerup", this.tapUpHandler);
      this.tapUpHandler = null;
    }
    this.isDragging = false;
  }

  private cleanupTimersAndTweens(): void {
    this.activeTimers.forEach((t) => t.remove());
    this.activeTimers = [];
    this.activeTweens.forEach((t) => t.stop());
    this.activeTweens = [];
  }

  hide(): void {
    if (this.dataListener) {
      window.socket?.off("season-data", this.dataListener as any);
      this.dataListener = null;
    }
    this.portalSpinner?.destroy();
    this.portalSpinner = null;
    this.cleanupScrollHandlers();
    this.cleanupTimersAndTweens();
    this.panel.setVisible(false);
  }

  destroy(): void {
    this.hide();
  }
}
